/**
 * /promos/$id/edit — edit an existing slot override.
 *
 * `pageSlug` and `slotId` are NOT editable (to move a PR to a different
 * slot, admin deletes + recreates). Only `source` and the time window
 * can change. Window patches re-run overlap check at the API layer.
 */

import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import type {
  HydratedArticleItem,
  SlotOverrideSource,
  SlotOverrideUpdateInput,
} from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArticlePickerDialog } from '@/components/group-contents/article-picker-dialog';
import { useSession } from '@/lib/auth';
import { datetimePickerHandlers } from '@/lib/datetime-picker';
import { hasPermission } from '@/lib/permissions';
import { slotOverrideDetailQueryOptions, useUpdateSlotOverride } from '@/lib/slot-overrides';

export const Route = createFileRoute('/_authenticated/promos/$id/edit')({
  component: EditPromoPage,
});

type SourceKind = 'article' | 'inline';

/** Convert ISO → "YYYY-MM-DDTHH:mm" in local TZ for <input type=datetime-local>. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function EditPromoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const canManage = hasPermission(user, 'promos.manage');

  const detailQuery = useQuery(slotOverrideDetailQueryOptions(id));
  const update = useUpdateSlotOverride(id);

  const [sourceKind, setSourceKind] = useState<SourceKind>('article');
  const [pickedArticle, setPickedArticle] = useState<HydratedArticleItem | null>(null);
  const [originalPostId, setOriginalPostId] = useState<string | null>(null);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineThumb, setInlineThumb] = useState('');
  const [inlineLink, setInlineLink] = useState('');
  const [startsAtLocal, setStartsAtLocal] = useState('');
  const [endsAtLocal, setEndsAtLocal] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Sync form from server on first load.
  useEffect(() => {
    const row = detailQuery.data;
    if (!row) return;
    setSourceKind(row.source.kind);
    if (row.source.kind === 'article') {
      setOriginalPostId(row.source.postId);
      // Hold only the id; admin can re-pick the article for a fresh card render.
      setPickedArticle({
        type: 'article',
        id: row.source.postId,
        slug: '',
        title: '(บทความเดิม — กดเลือกใหม่เพื่อดูชื่อ)',
        summary: null,
        coverImageUrl: null,
        publishedAt: null,
        categorySlug: null,
        categoryName: null,
      });
    } else {
      setInlineTitle(row.source.title);
      setInlineThumb(row.source.thumbUrl ?? '');
      setInlineLink(row.source.linkUrl);
    }
    setStartsAtLocal(isoToLocalInput(row.startsAt));
    setEndsAtLocal(isoToLocalInput(row.endsAt));
  }, [detailQuery.data]);

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี <code>promos.manage</code>
      </div>
    );
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return <div className="p-8 text-center text-sm text-slate-400">กำลังโหลด PR…</div>;
  }
  const row = detailQuery.data;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!startsAtLocal || !endsAtLocal) {
      setFormError('กรุณาเลือกช่วงเวลา');
      return;
    }
    const startsAt = new Date(startsAtLocal).toISOString();
    const endsAt = new Date(endsAtLocal).toISOString();
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setFormError('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม');
      return;
    }

    let source: SlotOverrideSource;
    if (sourceKind === 'article') {
      const postId = pickedArticle?.id ?? originalPostId;
      if (!postId) {
        setFormError('กรุณาเลือกบทความ');
        return;
      }
      source = { kind: 'article', postId };
    } else {
      if (!inlineTitle.trim()) {
        setFormError('กรุณาใส่หัวข้อ');
        return;
      }
      if (!inlineLink.trim()) {
        setFormError('กรุณาใส่ลิงก์');
        return;
      }
      source = {
        kind: 'inline',
        title: inlineTitle.trim(),
        thumbUrl: inlineThumb.trim() || null,
        linkUrl: inlineLink.trim(),
      };
    }

    const payload: SlotOverrideUpdateInput = {
      source,
      startsAt,
      endsAt,
    };
    update.mutate(payload, {
      onSuccess: () => {
        void navigate({ to: '/promos' });
      },
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/promos" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            <ArrowLeft className="mr-1 h-4 w-4" /> กลับ
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            แก้ไข PR
          </h1>
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
        >
          {/* Locked fields */}
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-700 dark:bg-slate-800/50">
            <div className="text-slate-500">หน้า / เป้าหมาย (แก้ไม่ได้):</div>
            <div className="mt-0.5 font-medium">
              <code>/{row.pageSlug}</code> ·{' '}
              {row.slideIndex === null ? (
                <>
                  slot <code>{row.slotId}</code>
                </>
              ) : (
                <>
                  carousel <code>{row.slotId}</code>{' '}
                  <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                    slide #{row.slideIndex}
                  </span>
                </>
              )}
            </div>
            <div className="mt-1 text-[10px] text-slate-400">
              ต้องการย้ายเป้าหมาย → ลบ PR นี้แล้วสร้างใหม่
            </div>
          </div>

          {/* Source kind tabs */}
          <div className="space-y-2">
            <Label>แหล่งเนื้อหา</Label>
            <div className="flex border-b border-slate-200 dark:border-slate-700">
              {(['article', 'inline'] as const).map((k) => {
                const active = sourceKind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSourceKind(k)}
                    className={`px-3 py-1.5 text-xs transition ${
                      active
                        ? 'border-b-2 border-amber-500 text-amber-700 dark:text-amber-300'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                  >
                    {k === 'article' ? '📰 บทความใน CMS' : '🔗 Inline'}
                  </button>
                );
              })}
            </div>

            {sourceKind === 'article' && (
              <div className="pt-1">
                {pickedArticle ? (
                  <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                    <div className="font-medium">{pickedArticle.title}</div>
                    {pickedArticle.slug && (
                      <div className="text-xs text-slate-500">/{pickedArticle.slug}</div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setArticlePickerOpen(true)}
                      className="mt-1.5"
                    >
                      เปลี่ยน
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setArticlePickerOpen(true)}
                  >
                    เลือกบทความ…
                  </Button>
                )}
              </div>
            )}

            {sourceKind === 'inline' && (
              <div className="space-y-2 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="inline-title" className="text-xs">
                    หัวข้อ
                  </Label>
                  <Input
                    id="inline-title"
                    value={inlineTitle}
                    onChange={(e) => setInlineTitle(e.target.value)}
                    maxLength={200}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inline-thumb" className="text-xs">
                    Thumbnail URL (optional)
                  </Label>
                  <Input
                    id="inline-thumb"
                    value={inlineThumb}
                    onChange={(e) => setInlineThumb(e.target.value)}
                    placeholder="https://…"
                    maxLength={2000}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="inline-link" className="text-xs">
                    ลิงก์
                  </Label>
                  <Input
                    id="inline-link"
                    value={inlineLink}
                    onChange={(e) => setInlineLink(e.target.value)}
                    placeholder="https://…"
                    maxLength={2000}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Window */}
          <div className="space-y-2">
            <Label>ช่วงเวลา</Label>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="starts-at" className="text-xs">
                  เริ่ม
                </Label>
                <Input
                  id="starts-at"
                  type="datetime-local"
                  value={startsAtLocal}
                  onChange={(e) => setStartsAtLocal(e.target.value)}
                  {...datetimePickerHandlers}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ends-at" className="text-xs">
                  สิ้นสุด
                </Label>
                <Input
                  id="ends-at"
                  type="datetime-local"
                  value={endsAtLocal}
                  onChange={(e) => setEndsAtLocal(e.target.value)}
                  {...datetimePickerHandlers}
                />
              </div>
            </div>
          </div>

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={update.isPending}>
              <Save className="mr-1 h-4 w-4" />
              {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
            </Button>
          </div>
        </form>
      </div>

      <ArticlePickerDialog
        open={articlePickerOpen}
        onOpenChange={setArticlePickerOpen}
        pressReleaseOnly
        onPick={(article) => {
          setPickedArticle(article);
          setArticlePickerOpen(false);
        }}
      />
    </div>
  );
}
